import type { PairReferencePnl, PairReferencePnlStockLeg } from '../services/pairTradingApi';
import type { PairPnlStockLeg } from './pairReferencePnl';
import type { PositionPnlLeg } from './pairPositionPnl';

/**
 * Which number a per-leg PnL cell should show, and where it came from.
 *
 * Three sources, in strict priority order:
 *   1. `position` — the leg has a fill. This is REAL money: (live price − average
 *      entry) × qty held. Nothing hypothetical can outrank it.
 *   2. `engine`   — flat leg, engine reporting. The OPEN trigger's own per-leg
 *      PnL for ONE pair, read from the engine (never recomputed here), i.e. what
 *      this leg would make if the pair opened now at the reference prices.
 *   3. `preview`  — flat leg, engine silent (bot stopped). Same arithmetic done
 *      client-side so the operator can sanity-check reference prices before
 *      Start. Must be labelled as a preview; it is the only source that can
 *      disagree with the bot.
 *
 * Sources 2 and 3 are PER PAIR (one contract) and are NOT scaled by allocated
 * cash — the caller must say so on screen, or the number reads as a position PnL.
 */
export type PnlSource = 'position' | 'engine' | 'preview';

export interface PnlCell {
    source: PnlSource;
    /** Raw VND. Null when this source has the leg but cannot value it. */
    pnl: number | null;
    /** Shares/contracts the number is measured on. */
    quantity: number | null;
    /** Long/short of the leg, when the source knows it. */
    side?: 'LONG' | 'SHORT';
    /** Average entry, `position` source only (leg's own display unit). */
    averageCost?: number;
    /** True on `position` when no live tick has arrived and the BE mark is used. */
    stale?: boolean;
    /** Why the number is 0 or absent — surfaced in the tooltip. */
    note?: string;
}

export interface ResolvePnlCellInput {
    /** Open position for this leg, if any. */
    position?: PositionPnlLeg;
    /** Matching leg from the engine snapshot, if it is reporting. */
    engineLeg?: { pnl: number | null; quantity: number | null; side?: 'LONG' | 'SHORT'; note?: string | null };
    /** Matching leg from the local preview, if computed. */
    previewLeg?: PairPnlStockLeg;
    /** Engine snapshot — consulted for `undefinedReason` and `reporting`. */
    engine?: PairReferencePnl | null;
}

/**
 * @returns the cell to render, or null when no source has anything to say
 *          (flat leg, no engine, no preview) — the caller renders "—".
 */
export function resolvePnlCell({
    position,
    engineLeg,
    previewLeg,
    engine,
}: ResolvePnlCellInput): PnlCell | null {
    // A fill outranks everything. `quantity` is already the open size summed
    // across pairs, so 0 means flat and the hypothetical sources take over.
    if (position && position.quantity !== 0) {
        return {
            source: 'position',
            pnl: position.pnl,
            quantity: position.quantity,
            side: position.side,
            averageCost: position.averageCost,
            stale: !position.live,
        };
    }

    if (engine?.reporting && engineLeg) {
        return {
            source: 'engine',
            pnl: engineLeg.pnl,
            quantity: engineLeg.quantity,
            side: engineLeg.side,
            note: engineLeg.note ?? engine.undefinedReason ?? undefined,
        };
    }

    if (previewLeg) {
        return {
            source: 'preview',
            pnl: previewLeg.pnl,
            quantity: previewLeg.quantity,
            note: previewLeg.note,
        };
    }

    return null;
}

/** Headline trigger value plus where it came from and how trustworthy it is. */
export interface ReferencePnlSource {
    source: 'engine' | 'preview';
    /** Raw VND, or null when the trigger is undefined. */
    total: number | null;
    undefinedReason: string | null;
    blockedReason: string | null;
    ageSeconds: number | null;
    /**
     * Preview because the trigger inputs carry unsaved edits, not because the bot
     * is stopped. The bot is still acting on the saved reference.
     */
    unsavedEdits: boolean;
}

/**
 * Decide which reference-PnL number the screen should show.
 *
 * The engine's number is authoritative but it is computed from the SAVED
 * reference prices. So it can only be shown when the form agrees with what was
 * saved — otherwise typing a reference price would leave the number untouched,
 * which makes the field look broken and, worse, invites the operator to Start on
 * a number that was never about their input.
 *
 * @param published    engine snapshot, or null when it has not been fetched
 * @param preview      client-side breakdown of the values currently in the form
 * @param inputsDirty  the trigger inputs (side, futures reference, per-stock
 *                     weight + reference) differ from the saved baseline
 */
export function resolveReferenceSource(
    published: PairReferencePnl | null | undefined,
    preview: { total: number | null; undefinedReason: string | null },
    inputsDirty: boolean,
): ReferencePnlSource {
    if (published?.reporting && !inputsDirty) {
        return {
            source: 'engine',
            total: published.total,
            undefinedReason: published.undefinedReason,
            blockedReason: published.blockedReason,
            ageSeconds: published.ageSeconds,
            unsavedEdits: false,
        };
    }
    return {
        source: 'preview',
        total: preview.total,
        undefinedReason: preview.undefinedReason,
        blockedReason: null,
        ageSeconds: null,
        // Only "unsaved edits" when a bot IS reporting — otherwise the preview is
        // simply the only source there is, and calling that unsaved would be noise.
        unsavedEdits: inputsDirty && Boolean(published?.reporting),
    };
}

/** Bare ticker of an exchange-qualified instrument id ("HNXDS:VN30F2609"). */
export const tickerOf = (id: string): string => id.split(':').pop() ?? id;

/** Engine stock legs keyed by bare ticker, for row lookup. */
export function engineStocksByTicker(
    engine: PairReferencePnl | null,
): Record<string, PairReferencePnlStockLeg> {
    const out: Record<string, PairReferencePnlStockLeg> = {};
    for (const leg of engine?.stocks ?? []) {
        out[tickerOf(leg.instrument || leg.symbol)] = leg;
    }
    return out;
}

/**
 * Human-readable form of the engine's `undefinedReason` / `blockedReason`
 * vocabulary. Unknown codes fall through verbatim rather than being hidden — a
 * new engine reason must not silently render as "everything is fine".
 */
export function explainReferenceReason(code: string | null | undefined): string | null {
    if (!code) return null;
    if (code.startsWith('no_stock_price:')) {
        return `no live quote for ${code.slice('no_stock_price:'.length)}`;
    }
    if (code.startsWith('no_stock_reference:')) {
        return `no reference price set for ${code.slice('no_stock_reference:'.length)}`;
    }
    switch (code) {
        case 'no_future_reference':
            return 'no futures reference price set';
        case 'no_future_price':
            return 'no live futures quote';
        case 'reference_unusable':
            return 'reference belongs to another contract, or is >5% from the live price — the bot will not open';
        case 'paused':
            return 'opening is paused (is_pause)';
        case 'withdraw_pending':
            return 'a withdraw is pending — no new pairs until it settles';
        case 'rolled_awaiting_expiry':
            return 'futures leg already rolled — no new pairs until the previous contract expires';
        case 'not_time_to_open':
            return 'outside the opening window';
        case 'max_open':
            return 'max open contracts reached';
        case 'pnl_nonneg':
            return 'PnL ≥ 0 — no entry signal';
        case 'volume_lease':
            return 'waiting for the fleet-wide open slot';
        default:
            return code;
    }
}
